import React, { useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import DynamicTable from '../../components/Table/Table';
import ConfirmationModal from '../../components/Modals/ConfirmationModal';
import {
  deleteUploadedData,
  fetchRecentlyUploadData,
  fetchRecentlyUploadDataStatusCheck
} from './services';
import { format } from 'date-fns-tz';
import refreshIcon from '../../assests/images/refresh.svg';
import { useSelector } from 'react-redux';

const RecentlyUploadedTable = ({ refreshTrigger, optionByBU, uploadedData }) => {
  const [data, setData] = useState([]);
  const [uploadIdArr, setUploadIdArr] = useState([]);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteData, setDeleteData] = useState({});
  const selectedBu = useSelector((state) => state?.cycle?.selectedBu);
  const [marketName, setMarketName] = useState('');

  useEffect(() => {
    if (selectedBu) {
      setMarketName(selectedBu);
      getRecentlyUploadData(selectedBu);
    }
  }, [selectedBu, refreshTrigger, optionByBU]);

  const columns = [
    { id: 'fileName', label: 'NAME', type: 'text' },
    { id: 'userName', label: 'UPLOADED BY', type: 'text' },
    { id: 'uploadedDate', label: 'UPLOADED DATE', type: 'text' },
    { id: 'uploadedTime', label: 'UPLOADED TIME', type: 'text' },
    { id: 'uploadStatus', label: 'STATUS', type: 'status' },
    { id: 'typeOfData', label: 'TYPE OF DATA', type: 'text' }
  ];

  const handleDelete = async () => {
    try {
      await deleteUploadedData(deleteData.typeOfData, deleteData.uploadId, deleteData.fileName);
      getRecentlyUploadData(marketName);
    } catch (error) {
      // Handle error
    } finally {
      setIsDeleteModalVisible(false);
      setDeleteData(null);
    }
  };

  const filterTableData = (data) => {
    // log set of all unique type of data
    const filteredData = data?.filter((val) =>
      optionByBU?.some(
        (option) => option?.value?.trim()?.toLowerCase() == val?.fileName?.trim()?.toLowerCase()
      )
    );
    return filteredData;
  };

  const getRecentlyUploadData = async (market) => {
    setIsTableLoading(true);
    try {
      const { content } = await fetchRecentlyUploadData(market);

      content.forEach((val) => {
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const date = new Date(val.uploadDateTime * 1000);
        const timeZoneAbbreviation = format(date, 'zzz', { timeZone });

        const options = {
          timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        };
        const formattedDate = date.toLocaleString('en-US', options);
        const [datePart, timePart] = formattedDate.split(', ');

        val['uploadedDate'] = datePart;
        const [time, modifier] = timePart.split(' ');
        const [hours, minutes] = time.split(':');
        val['uploadedTime'] = `${hours}:${minutes} ${modifier} ${timeZoneAbbreviation}`;
      });

      const processingIDs = content
        .filter((response) => response.uploadStatus !== 'Failed' || response.status !== 'Success')
        .map((response) => response.uploadId);

      setData(filterTableData(content));
      uploadedData(filterTableData(content));
      if (processingIDs.length > 0) {
        setUploadIdArr(processingIDs);
      }
    } catch (error) {
      // Handle error
    } finally {
      setIsTableLoading(false);
    }
  };

  const getUploadStatusForRows = async () => {
    try {
      // Fetch the current upload status
      const statusVal = await fetchRecentlyUploadDataStatusCheck(uploadIdArr);

      if (!statusVal) return;

      // Separate IDs based on their status
      const completedProcessingIDs = [];
      const inProgressIDs = [];

      statusVal.forEach(({ status, uploadId }) => {
        if (['Failed', 'Success', 'Uploaded'].includes(status)) {
          completedProcessingIDs.push(uploadId);
        } else if (status === 'In Progress') {
          inProgressIDs.push(uploadId);
        }
      });

      // Update statuses in the data array
      const updatedData = data.map((item) => {
        const statusEntry = statusVal.find((entry) => entry.uploadId === item.uploadId);
        return statusEntry ? { ...item, uploadStatus: statusEntry.status } : item;
      });

      // Calculate the IDs to check in the next update
      const idsToCheck = [
        ...inProgressIDs,
        ...uploadIdArr.filter((id) => !completedProcessingIDs.includes(id))
      ];

      // Update the state
      setData(filterTableData(updatedData));
      uploadedData(filterTableData(updatedData));
      setUploadIdArr(idsToCheck);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsTableLoading(false);
    }
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (uploadIdArr.length > 0) getUploadStatusForRows();
    }, 30000);

    return () => clearInterval(intervalId);
  }, [uploadIdArr]);

  return (
    <Box
      component="section"
      sx={{
        p: 2,
        borderRadius: '8px',
        backgroundColor: '#FBFCFE',
        boxShadow: '4px 0px 24px -2px #005CBC14'
      }}>
      <Button
        onClick={() => getRecentlyUploadData(marketName)}
        data-testid="refresh-button-test"
        style={{ float: 'right', position: 'relative', left: '-326px', zIndex: '1000' }}>
        <img src={refreshIcon} alt="refreshIcon" />
      </Button>
      {marketName ? (
        <DynamicTable
          originalColumns={columns}
          originalData={data}
          tableName="Recently Uploaded"
          isTableLoading={isTableLoading}
          isSearch
        />
      ) : (
        <p>Loading...</p>
      )}
      {isDeleteModalVisible && (
        <ConfirmationModal
          open={isDeleteModalVisible}
          title="Delete data?"
          text="Are you sure you want to permanently remove this item?"
          cancelText="Cancel"
          SubmitText="Delete"
          onClose={() => {
            setIsDeleteModalVisible(false);
            setDeleteData(null);
          }}
          onSubmit={handleDelete}
        />
      )}
    </Box>
  );
};

export default RecentlyUploadedTable;
